import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../config';
import { s } from './TissStyles';
import { opLabels, localLabel, statusColors } from './TissConstants';
import { formatDateTime, formatDuration, downloadXml } from './TissHelpers';
import { StatusBadge, SummaryField, XmlViewer, ToastProvider, useToast } from './TissShared';

// ==================== LOTE STATUS FLOW ====================

const LOTE_FLOW = ['pendente', 'enviado', 'aceito'];

function LoteStatusFlow({ currentStatus }) {
  const rejected = ['erro', 'rejeitado', 'cancelado'].includes(currentStatus);
  const steps = rejected ? [...LOTE_FLOW.slice(0, 2), currentStatus] : LOTE_FLOW;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginBottom: '16px' }}>
      {steps.map((step, i) => {
        const isCurrent = step === currentStatus;
        const isPast = !rejected && LOTE_FLOW.indexOf(currentStatus) > LOTE_FLOW.indexOf(step);
        const isActive = isCurrent || isPast;
        const sc = statusColors[step] || { bg: '#f5f5f5', color: '#999', label: step };

        return (
          <React.Fragment key={step}>
            {i > 0 && (
              <div style={{ width: '40px', height: '2px', backgroundColor: isActive || isPast ? sc.color : '#e0e0e0' }} />
            )}
            <div style={{
              padding: '4px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: isCurrent ? '700' : '500',
              backgroundColor: isCurrent ? sc.bg : isPast ? '#f5f5f5' : '#fafafa',
              color: isCurrent ? sc.color : isPast ? '#666' : '#bbb',
              border: `1.5px solid ${isCurrent ? sc.color : isPast ? '#ccc' : '#e0e0e0'}`,
              whiteSpace: 'nowrap',
            }}>
              {sc.label}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ==================== MAIN PAGE ====================

function LoteDetalhesInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [lote, setLote] = useState(null);
  const [transacoes, setTransacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Timeline XML lazy load
  const [expandedTx, setExpandedTx] = useState(null);
  const [txXml, setTxXml] = useState({});
  const [loadingXml, setLoadingXml] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/tiss/lotes/${id}/timeline`);
      setLote(res.data.lote);
      setTransacoes(res.data.transacoes || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar detalhes do lote');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ==================== ACTIONS ====================

  const handleAction = async (action, label) => {
    setActionLoading(action);
    try {
      const res = await axios.post(`${API_URL}/tiss/lotes/${id}/${action}`);
      // Refazer cria OUTRO lote: o retorno é sobre ele, não sobre este. Leva o operador
      // para lá, senão ele fica olhando o lote antigo achando que algo mudou aqui.
      if (action === 'remontar' && res.data.lote?.id) {
        toast.success(res.data.message);
        navigate(`/tiss-unimed/lote/${res.data.lote.id}`);
        return;
      }
      const statusLabel = statusColors[res.data.lote?.status]?.label || res.data.lote?.status || 'OK';
      toast.success(`${label}: ${statusLabel}`);
      await fetchData();
    } catch (err) {
      // detalhes traz o motivo da operadora (ex.: "5012 - RECEBIMENTO DE MENSAGEM NAO
      // FINALIZADO"); sem ele o operador só via "Operadora recusou a consulta".
      const { error: erro, detalhes } = err.response?.data || {};
      toast.error(`Erro ao ${label.toLowerCase()}: ${detalhes || erro || err.message}`);
      await fetchData(); // recarrega para exibir o ultimo_erro que o backend acabou de gravar
    } finally {
      setActionLoading(null);
    }
  };

  // ==================== TIMELINE XML LAZY LOAD ====================

  const loadTxXml = async (txId) => {
    if (txXml[txId]) return;
    setLoadingXml(txId);
    try {
      const { data: txData } = await axios.get(`${API_URL}/tiss/transacoes/${txId}`);
      setTxXml(prev => ({ ...prev, [txId]: { xml_request: txData.xml_request, xml_response: txData.xml_response } }));
    } catch (err) {
      console.error('Erro ao carregar XML:', err);
    } finally {
      setLoadingXml(null);
    }
  };

  const handleExpandTx = (txId) => {
    if (expandedTx === txId) { setExpandedTx(null); return; }
    setExpandedTx(txId);
    loadTxXml(txId);
  };

  // ==================== RENDER ====================

  if (loading) {
    return (
      <div style={s.page} className="data-page">
        <div style={{ textAlign: 'center', padding: '80px 20px', color: '#888' }}>
          <div style={{ fontSize: '16px', marginBottom: '8px' }}>Carregando detalhes do lote...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={s.page} className="data-page">
        <div style={s.breadcrumb}>
          <a href="/" style={s.breadcrumbLink}>Inicio</a> / <a href="/tiss-unimed" style={s.breadcrumbLink}>TISS UNIMED</a> / Lote
        </div>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>!</div>
          <div style={{ fontSize: '16px', color: '#c62828', marginBottom: '8px' }}>{error}</div>
          <button style={s.btnSecondary} onClick={() => navigate('/tiss-unimed')}>Voltar</button>
        </div>
      </div>
    );
  }

  const guias = lote?.guias || [];
  const sectionTitle = { fontSize: '14px', fontWeight: '600', color: '#1a1a2e', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' };
  const sectionCard = { ...s.card, padding: '20px 24px' };

  // Calcular resumo de guias por status
  const guiaStatusCount = {};
  guias.forEach(g => { guiaStatusCount[g.status] = (guiaStatusCount[g.status] || 0) + 1; });

  // Valor total das guias (somar dos procedimentos se disponivel)
  const valorTotal = lote.valor_total ? Number(lote.valor_total) : 0;

  return (
    <div style={s.page} className="data-page">
      {/* Breadcrumb */}
      <div style={s.breadcrumb}>
        <a href="/" style={s.breadcrumbLink}>Inicio</a> / <a href="/tiss-unimed" style={s.breadcrumbLink}>TISS UNIMED</a> / Lote {lote.numero_lote}
      </div>

      {/* Header */}
      <div style={{ ...s.pageHeader, marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => navigate('/tiss-unimed')} style={{ ...s.btnSecondary, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
            &#8592; Voltar
          </button>
          <div>
            <h1 style={{ ...s.pageTitle, fontSize: '1.3rem' }}>Lote {lote.numero_lote}</h1>
            <p style={s.pageSubtitle}>
              {localLabel(lote.local_id)} | {guias.length} guia{guias.length !== 1 ? 's' : ''}
              {lote.numero_protocolo_operadora && (
                <span style={{ fontFamily: 'monospace', marginLeft: '8px' }}>
                  | Protocolo: {lote.numero_protocolo_operadora}
                </span>
              )}
            </p>
          </div>
        </div>
        <StatusBadge status={lote.status} />
      </div>

      {/* Status flow */}
      <div style={{ ...s.card, padding: '12px 24px', marginBottom: '16px' }}>
        <LoteStatusFlow currentStatus={lote.status} />
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {lote.status === 'pendente' && (
          <button style={s.btnPrimary} disabled={!!actionLoading}
            onClick={() => handleAction('enviar', 'Enviar Lote')}>
            {actionLoading === 'enviar' ? 'Enviando...' : 'Enviar Lote'}
          </button>
        )}
        {lote.status === 'enviado' && (
          <button style={s.btnSecondary} disabled={!!actionLoading}
            onClick={() => handleAction('status', 'Verificar Status')}>
            {actionLoading === 'status' ? 'Verificando...' : 'Verificar Status'}
          </button>
        )}
        {/* Refazer com os dados atuais. Reenviar cola o XML congelado de cada guia, então
            correção de cadastro feita depois do envio NÃO entra no reenvio — só aqui. */}
        {!lote.substituido_por_lote_id && ['enviado', 'erro', 'rejeitado'].includes(lote.status) && (
          <button style={{ ...s.btnSecondary, color: '#00695c', borderColor: '#00695c' }} disabled={!!actionLoading}
            onClick={() => {
              const ok = window.confirm(
                `Refazer o lote ${lote.numero_lote} com os dados atuais do sistema?\n\n` +
                'Gera um lote NOVO com as mesmas guias, regerando o XML com os dados do sistema de agora ' +
                '(carteirinha, exames). As autorizações já existentes são mantidas.\n\n' +
                'Nada é enviado à UNIMED neste passo — depois é só conferir e usar "Enviar Lote".'
              );
              if (ok) handleAction('remontar', 'Remontar lote');
            }}>
            {actionLoading === 'remontar' ? 'Remontando…' : 'Remontar com dados atuais'}
          </button>
        )}
        {lote.substituido_por_lote_id && (
          <button style={{ ...s.btnSecondary, color: '#00695c', borderColor: '#00695c' }}
            onClick={() => navigate(`/tiss-unimed/lote/${lote.substituido_por_lote_id}`)}>
            Ver lote que substituiu este
          </button>
        )}

        {/* Reenvio tambem quando o lote consta "enviado" mas a operadora recusou a consulta
            (ex.: glosa 5012, recebimento nao finalizado) — era o caso sem saida pela tela. */}
        {(['erro', 'rejeitado'].includes(lote.status) || (lote.status === 'enviado' && lote.ultimo_erro)) && (
          <button style={{ ...s.btnSecondary, color: '#e65100', borderColor: '#e65100' }} disabled={!!actionLoading}
            onClick={() => {
              const ok = window.confirm(
                `Reenviar o lote ${lote.numero_lote} para a UNIMED?\n\n` +
                'Vai o mesmo numero de lote e as mesmas guias, entao nao gera cobranca em duplicidade — ' +
                'mas a operadora pode devolver um novo numero de protocolo.\n\n' +
                'Use quando a UNIMED nao finalizou o recebimento do lote.'
              );
              if (ok) handleAction('reenviar', 'Reenviar Lote');
            }}>
            {actionLoading === 'reenviar' ? 'Reenviando...' : 'Reenviar Lote'}
          </button>
        )}
        {lote.xml_request && (
          <button style={s.btnSecondary}
            onClick={() => downloadXml(lote.xml_request, `tiss_lote_${lote.numero_lote}_request.xml`)}>
            Download XML Request
          </button>
        )}
        {lote.xml_response && (
          <button style={s.btnSecondary}
            onClick={() => downloadXml(lote.xml_response, `tiss_lote_${lote.numero_lote}_response.xml`)}>
            Download XML Response
          </button>
        )}
      </div>

      {/* Section A: Resumo do Lote */}
      <div style={sectionCard}>
        <div style={sectionTitle}>Dados do Lote</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px 20px' }}>
          <SummaryField label="Numero do Lote" value={lote.numero_lote} mono />
          <SummaryField label="Protocolo Operadora" value={lote.numero_protocolo_operadora || '-'} mono />
          <SummaryField label="Local" value={`${localLabel(lote.local_id)} (${lote.local_id})`} />
          <SummaryField label="Status" value={statusColors[lote.status]?.label || lote.status} />
          <SummaryField label="Total de Guias" value={String(lote.total_guias || guias.length)} />
          <SummaryField label="Valor Total" value={valorTotal ? `R$ ${valorTotal.toFixed(2)}` : '-'} mono />
          <SummaryField label="Enviado em" value={formatDateTime(lote.enviado_em)} />
          <SummaryField label="Criado em" value={formatDateTime(lote.created_at)} />
          {lote.origem_lote_id && <SummaryField label="Refeito a partir do lote" value={`#${lote.origem_lote_id}`} />}
          {lote.substituido_por_lote_id && <SummaryField label="Substituído pelo lote" value={`#${lote.substituido_por_lote_id}`} />}
        </div>
        {(lote.total_aceitas != null || lote.total_rejeitadas != null || lote.valor_glosado != null) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px 20px', marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #f0f0f0' }}>
            <SummaryField label="Aceitas" value={lote.total_aceitas != null ? String(lote.total_aceitas) : '-'} />
            <SummaryField label="Rejeitadas" value={lote.total_rejeitadas != null ? String(lote.total_rejeitadas) : '-'} />
            <SummaryField label="Valor Glosado" value={lote.valor_glosado ? `R$ ${Number(lote.valor_glosado).toFixed(2)}` : '-'} mono />
            <SummaryField label="Atualizado em" value={formatDateTime(lote.updated_at)} />
          </div>
        )}
      </div>

      {/* Situacao na operadora (Tabela 47 ANS) — o status interno nao diz se a UNIMED
          recebeu de fato; sem isso o lote aparecia so como "Enviada / 0 aceitas". */}
      {lote.situacao_consultada_em && (
        <div style={{
          ...sectionCard,
          backgroundColor: lote.situacao_operadora_codigo ? '#e8f5e9' : '#fff8e1',
          borderLeft: `4px solid ${lote.situacao_operadora_codigo ? '#2e7d32' : '#e65100'}`,
        }}>
          <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '14px', color: lote.situacao_operadora_codigo ? '#2e7d32' : '#e65100' }}>
            Situação na UNIMED
          </div>
          <div style={{ fontSize: '13px', color: '#555' }}>
            {lote.situacao_operadora_label || 'Sem informação'}
            {lote.situacao_operadora_codigo ? ` (código ${lote.situacao_operadora_codigo})` : ''}
            {' — consultado em '}{formatDateTime(lote.situacao_consultada_em)}
          </div>
        </div>
      )}

      {/* Error display */}
      {lote.ultimo_erro && (
        <div style={{ ...sectionCard, backgroundColor: '#fce4ec', borderLeft: '4px solid #c62828' }}>
          <div style={{ fontWeight: '600', color: '#c62828', marginBottom: '4px', fontSize: '14px' }}>Erro no envio</div>
          <div style={{ fontSize: '13px', color: '#555', whiteSpace: 'pre-wrap' }}>{lote.ultimo_erro}</div>
        </div>
      )}

      {/* Section B: Guias do Lote */}
      <div style={sectionCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={sectionTitle}>
            Guias do Lote ({guias.length})
          </div>
          {Object.keys(guiaStatusCount).length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {Object.entries(guiaStatusCount).map(([st, count]) => {
                const sc = statusColors[st] || { bg: '#f5f5f5', color: '#999', label: st };
                return (
                  <span key={st} style={{
                    fontSize: '11px', padding: '2px 10px', borderRadius: '12px',
                    backgroundColor: sc.bg, color: sc.color, fontWeight: '600',
                  }}>
                    {sc.label}: {count}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {guias.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#888', padding: '12px 0' }}>Nenhuma guia vinculada a este lote.</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: '40px' }}>#</th>
                <th style={s.th}>Guia Prestador</th>
                <th style={s.th}>Paciente</th>
                <th style={s.th}>Carteirinha</th>
                <th style={s.th}>LOCAL/Protocolo</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Autorizacao</th>
                <th style={{ ...s.th, width: '60px' }}></th>
              </tr>
            </thead>
            <tbody>
              {guias.map((g, i) => (
                <tr key={g.id} style={s.trHover}>
                  <td style={{ ...s.td, color: '#888' }}>{i + 1}</td>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontWeight: '500' }}>{g.numero_guia_prestador}</td>
                  <td style={s.td}>{g.paciente_nome}</td>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px' }}>{g.paciente_carteirinha}</td>
                  <td style={{ ...s.td, fontFamily: 'monospace' }}>{g.solicitacao_local}/{g.solicitacao_protocolo}</td>
                  <td style={s.td}><StatusBadge status={g.status} /></td>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px' }}>{g.numero_autorizacao || '-'}</td>
                  <td style={s.td}>
                    <button style={s.btnSmall}
                      onClick={() => navigate(`/tiss-unimed/protocolo/${g.solicitacao_local}/${g.solicitacao_protocolo}`)}>
                      Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Section C: Timeline */}
      {transacoes.length > 0 && (
        <div style={sectionCard}>
          <div style={sectionTitle}>
            Timeline de Eventos ({transacoes.length} evento{transacoes.length !== 1 ? 's' : ''})
          </div>

          <div style={{ position: 'relative', paddingLeft: '28px' }}>
            <div style={{ position: 'absolute', left: '9px', top: '8px', bottom: '8px', width: '2px', backgroundColor: '#e0e0e0' }} />
            {transacoes.map((tx, idx) => {
              const isExpanded = expandedTx === tx.id;
              const isOk = tx.sucesso;
              const dotColor = isOk ? '#2e7d32' : '#c62828';
              const borderColor = isOk ? '#c8e6c9' : '#ffcdd2';
              const httpColor = !tx.http_status ? '#999' : tx.http_status < 300 ? '#2e7d32' : tx.http_status < 400 ? '#e65100' : '#c62828';

              return (
                <div key={tx.id} style={{ position: 'relative', marginBottom: idx < transacoes.length - 1 ? '10px' : '0' }}>
                  <div style={{
                    position: 'absolute', left: '-24px', top: '12px',
                    width: '12px', height: '12px', borderRadius: '50%',
                    backgroundColor: dotColor, border: '2px solid #fff',
                    boxShadow: `0 0 0 2px ${dotColor}33`, zIndex: 1,
                  }} />
                  <div
                    onClick={() => handleExpandTx(tx.id)}
                    style={{
                      borderLeft: `4px solid ${isOk ? '#2e7d32' : '#c62828'}`,
                      backgroundColor: isExpanded ? '#fafafa' : '#fff',
                      border: `1px solid ${borderColor}`,
                      borderLeftWidth: '4px',
                      borderLeftColor: isOk ? '#2e7d32' : '#c62828',
                      borderRadius: '6px', padding: '10px 14px',
                      cursor: 'pointer', transition: 'background-color 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>
                          {opLabels[tx.operacao] || tx.operacao}
                        </span>
                        <span style={{
                          ...s.badge, fontSize: '10px', padding: '2px 8px',
                          backgroundColor: isOk ? '#e8f5e9' : '#fce4ec',
                          color: isOk ? '#2e7d32' : '#c62828',
                        }}>
                          {isOk ? 'Sucesso' : 'Erro'}
                        </span>
                        {tx.http_status && (
                          <span style={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: '600', color: httpColor }}>
                            HTTP {tx.http_status}
                          </span>
                        )}
                        {tx.duracao_ms != null && (
                          <span style={{ fontSize: '11px', color: '#888' }}>{formatDuration(tx.duracao_ms)}</span>
                        )}
                      </div>
                      <span style={{ fontSize: '11px', color: '#aaa' }}>{formatDateTime(tx.inicio)}</span>
                    </div>
                    {!isOk && tx.erro && (
                      <div style={{ fontSize: '12px', color: '#c62828', marginTop: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isExpanded ? 'normal' : 'nowrap' }}>
                        {isExpanded ? tx.erro : (tx.erro.length > 120 ? tx.erro.substring(0, 120) + '...' : tx.erro)}
                      </div>
                    )}
                    {isExpanded && (
                      <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '10px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px', marginBottom: '10px' }}>
                          <SummaryField label="Operacao" value={opLabels[tx.operacao] || tx.operacao} />
                          <SummaryField label="Inicio" value={formatDateTime(tx.inicio)} />
                          <SummaryField label="Fim" value={formatDateTime(tx.fim)} />
                          <SummaryField label="Duracao" value={formatDuration(tx.duracao_ms)} />
                          <SummaryField label="HTTP Status" value={tx.http_status ? String(tx.http_status) : '-'} mono />
                        </div>
                        {tx.endpoint && (
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Endpoint</div>
                            <div style={{ fontSize: '11px', fontFamily: 'monospace', color: '#555', wordBreak: 'break-all' }}>{tx.endpoint}</div>
                          </div>
                        )}
                        {tx.soap_fault && (
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>SOAP Fault</div>
                            <div style={{ fontSize: '12px', color: '#c62828', whiteSpace: 'pre-wrap' }}>{tx.soap_fault}</div>
                          </div>
                        )}
                        {tx.erro && (
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Erro completo</div>
                            <div style={{ fontSize: '12px', color: '#c62828', whiteSpace: 'pre-wrap' }}>{tx.erro}</div>
                          </div>
                        )}
                        {loadingXml === tx.id && (
                          <div style={{ textAlign: 'center', padding: '12px', color: '#888', fontSize: '12px' }}>Carregando XMLs...</div>
                        )}
                        {txXml[tx.id] && (
                          <>
                            {txXml[tx.id].xml_request && <XmlViewer xml={txXml[tx.id].xml_request} title="XML Request" />}
                            {txXml[tx.id].xml_response && <XmlViewer xml={txXml[tx.id].xml_response} title="XML Response" />}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Section D: XMLs do Lote */}
      {(lote.xml_request || lote.xml_response) && (
        <div style={sectionCard}>
          <div style={{ ...sectionTitle, marginBottom: '16px' }}>XMLs do Lote</div>
          {lote.xml_request && <XmlViewer xml={lote.xml_request} title="XML Enviado (Request)" />}
          {lote.xml_response && <XmlViewer xml={lote.xml_response} title="XML Resposta (Response)" />}
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
            {lote.xml_request && (
              <button style={{ ...s.btnSecondary, fontSize: '12px' }}
                onClick={() => downloadXml(lote.xml_request, `tiss_lote_${lote.numero_lote}_request.xml`)}>
                Download Request
              </button>
            )}
            {lote.xml_response && (
              <button style={{ ...s.btnSecondary, fontSize: '12px' }}
                onClick={() => downloadXml(lote.xml_response, `tiss_lote_${lote.numero_lote}_response.xml`)}>
                Download Response
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Wrap with ToastProvider
export default function LoteDetalhes() {
  return (
    <ToastProvider>
      <LoteDetalhesInner />
    </ToastProvider>
  );
}
